import { Inject } from '@odinjs/odin';
import { BarcodeAPI, BarcodeData, BaseToastProps, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { FragmentNames, ValidateHandlingOrderAliases } from '../../../codegen';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { AbortErrorInterface } from '../../../interfaces/double-conference/AbortErro';
import { ResponseDoubleConference } from '../../../interfaces/double-conference/doubleConference';
import { Patient } from '../../../interfaces/exam-collect/patiente-data-props';
import BaseController from '../../BaseController';
import { DoubleConferenceUtils } from '../utils/double-conference-utils';
import BarcodeService from '../../../services/barcode-service';

@Controller({ component: ValidateHandlingOrderAliases.VALIDATE_HANDLING_ORDER_LAYOUT, fragment: FragmentNames.VALIDATE_HANDLING_ORDER })
class ValidateHandlingOrderController extends BaseController {

  @Inject(ValidateHandlingOrderAliases.VALIDATE_HANDLING_ORDER_BARCODE)
  private barcodeApi!: BarcodeAPI;

  @Inject(ValidateHandlingOrderAliases.FORM_FETCH_AUXILIAR)
  private formFetchAuxiliar!: FormAPI;

  @OnMount(ValidateHandlingOrderAliases.VALIDATE_HANDLING_ORDER_LAYOUT)
  onLayoutMount(): void {
    const body = {
      expressionCode: 1073279,
      functionCode: 9136
    }
    HttpService.post(Endpoint.RECORD_AUDIT_LOG, {}, { body })
  }

  @OnMount(ValidateHandlingOrderAliases.VALIDATE_HANDLING_ORDER_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(ValidateHandlingOrderAliases.VALIDATE_HANDLING_ORDER_BARCODE)
  onReadSucesstBarCode(event: ReadEvent<BarcodeData>): void {
    const barcode = event.target as BarcodeAPI;
    this.validateBarcode(event);

    const params = { numberOrderManipulation: Number(event.item.value.trim()) };
    barcode.close();
    barcode.setEnabled(false);
    this.formFetchAuxiliar.setLoading(true);

    HttpService.get(Endpoint.TAG_MANIPULATION_VALIDATION, params).then((resp) => {
      const responseBody = resp.body as ResponseDoubleConference;
      this.formFetchAuxiliar.setLoading(false);
      barcode.setEnabled(true);
      if (responseBody.message) {
        this.showToast(responseBody.message, ToastLevel.WARNING);
        barcode.setOpen(true);
      } else if (responseBody.isSolution && responseBody.tagConferenceResponse.solutionSequenceNumber != null) {
        this.getPatientAndRedirect(
          String(responseBody.tagConferenceResponse.encounterNumber),
          params.numberOrderManipulation,
          responseBody.tagConferenceResponse.processSequenceNumber,
          responseBody.tagConferenceResponse.prescriptionNumber,
          responseBody.tagConferenceResponse.solutionSequenceNumber);
      } else {
        this.getPatientAndRedirect(
          String(responseBody.tagConferenceResponse.encounterNumber),
          params.numberOrderManipulation,
          responseBody.tagConferenceResponse.processSequenceNumber);
      }
    }).catch(error => {
      this.formFetchAuxiliar.setLoading(false);
      barcode.setEnabled(true);
      barcode.setOpen(true);
      this.tryToShowAbortMessages(error as AbortErrorInterface);
    })
  }

  validateBarcode(event: ReadEvent<BarcodeData>): void {
    const { preventDefault } = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
  }

  getPatientAndRedirect(encounterId: string, numberOrderManipulation: number, processSequenceNumber: number, numberPrescription?: number, solutionSequence?: number): void {
    const queryParams = `?nrAtendimento=${encounterId}&isAudit=false&page=0&limit=12`;
    const route = Endpoint.GET_PATIENT_LIST + queryParams;

    HttpService.get(route).then(resp => {
      const body = resp.body as Patient[];

      if (body.length === 0) {
        return Promise.reject();
      }
      const patientId = body[0].cdPessoaFisica;
      this.redirectToHandlingOrderItemsScreen(encounterId, patientId, numberOrderManipulation, processSequenceNumber, numberPrescription, solutionSequence);
      return Promise.resolve();
    }).catch(() => {
      this.barcodeApi.setOpen(true);
      this.showErrorToast(MessageService.translate('text.double-conference.not-allowed'));
    });
  }

  redirectToHandlingOrderItemsScreen(
    encounterId: string,
    patientId: string,
    numberOrderManipulation: number,
    processSequenceNumber: number,
    numberPrescription?: number,
    solutionSequence?: number): void {

    const paramsRoute = {
      encounterId: encounterId,
      numberOrderManipulation: numberOrderManipulation,
      patientId: patientId,
      processSequenceNumber: processSequenceNumber
    };

    DoubleConferenceUtils.validateParams(paramsRoute, numberPrescription, solutionSequence);

    applicationContext.redirect(ApplicationRoute.HANDLING_ORDER_ITEMS, paramsRoute);
  }

  tryToShowAbortMessages(abortError: AbortErrorInterface): void {
    if (abortError.body && abortError.body.errors) {
      abortError.body.errors.forEach((errorMessage) =>
        this.showToast(errorMessage, ToastLevel.ERROR)
      );
    }
  }

  showErrorToast(message: string): void {
    const toastErrorId = 'search-patient-controller.no-access-to-encounter';
    const toast: BaseToastProps = {
      dismissible: true,
      id: toastErrorId,
      level: ToastLevel.ERROR,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    };
    ToastService.open(toast)
  }

  showToast(message: string, level: ToastLevel): void {
    const toastId = message;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: level,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }
}

export default ValidateHandlingOrderController;
